import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../../components/icon/Icon";
import {
  buildGlobalSearchItems,
  filterGlobalSearchItems,
  getStoredSearchAccountActions,
} from "./searchIndex";
import { usePageSearch } from "./PageSearchContext";
import { buildCurrentPageSearchItems, filterCurrentPageSearchItems } from "./currentPageSearch";

const HeaderSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const observerTimeoutRef = useRef(null);
  const [accountActions, setAccountActions] = useState(() => getStoredSearchAccountActions());
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [pageSearchItems, setPageSearchItems] = useState([]);
  const { query, setQuery } = usePageSearch();

  const syncAccountActions = () => {
    setAccountActions(getStoredSearchAccountActions());
  };

  useEffect(() => {
    syncAccountActions();
    window.addEventListener("storage", syncAccountActions);
    window.addEventListener("authUpdated", syncAccountActions);

    return () => {
      window.removeEventListener("storage", syncAccountActions);
      window.removeEventListener("authUpdated", syncAccountActions);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setSelectedIndex(0);
  }, [location.pathname]);

  useEffect(() => {
    const syncPageSearchItems = () => {
      setPageSearchItems(buildCurrentPageSearchItems());
    };

    const root = document.querySelector("[data-global-search-root]");

    syncPageSearchItems();

    if (!root) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      window.clearTimeout(observerTimeoutRef.current);
      observerTimeoutRef.current = window.setTimeout(syncPageSearchItems, 120);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      window.clearTimeout(observerTimeoutRef.current);
    };
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    const handleShortcut = (event) => {
      const isEditableElement =
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA" ||
          event.target.isContentEditable);

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setIsOpen(true);
      } else if (event.key === "Escape") {
        setIsOpen(false);
      } else if (!isEditableElement && event.key === "/") {
        event.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };

    window.addEventListener("keydown", handleShortcut);

    return () => {
      window.removeEventListener("keydown", handleShortcut);
    };
  }, []);

  const searchItems = buildGlobalSearchItems(accountActions);
  const pageResults = filterCurrentPageSearchItems(query, pageSearchItems);
  const navigationResults = filterGlobalSearchItems(query, searchItems).map((item) => ({
    ...item,
    kind: "navigation",
    meta: [item.parent, item.category].filter(Boolean).join(" | "),
  }));
  const results = query ? [...pageResults, ...navigationResults].slice(0, 8) : navigationResults;

  useEffect(() => {
    if (selectedIndex > results.length - 1) {
      setSelectedIndex(0);
    }
  }, [results, selectedIndex]);

  const focusPageResult = (item) => {
    if (!(item?.element instanceof HTMLElement)) {
      return;
    }

    setIsOpen(false);
    setSelectedIndex(0);

    const target =
      item.element.closest(".nk-tb-item, .card, .card-inner, .data-list-row, h1, h2, h3, h4") || item.element;

    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    target.classList.add("global-search-highlight");
    window.setTimeout(() => {
      target.classList.remove("global-search-highlight");
    }, 1800);
  };

  const selectResult = (item) => {
    if (item?.kind === "page") {
      focusPageResult(item);
      return;
    }

    if (!item?.link) {
      return;
    }

    setIsOpen(false);
    setSelectedIndex(0);
    navigate(`${process.env.PUBLIC_URL}${item.link}`);
  };

  const handleInputKeyDown = (event) => {
    if (!results.length) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setSelectedIndex((currentIndex) => (currentIndex + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setSelectedIndex((currentIndex) => (currentIndex - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      selectResult(results[selectedIndex] || results[0]);
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className={`nk-header-search-panel${isOpen ? " is-open" : ""}`} ref={containerRef}>
      <div className="nk-header-search-surface">
        <span className="nk-header-search-icon">
          <Icon name="search" />
        </span>
        <input
          ref={inputRef}
          className="form-control border-transparent form-focus-none"
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
            setSelectedIndex(0);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleInputKeyDown}
          placeholder="Search anything"
        />
        <span className="nk-header-search-shortcut d-none d-lg-inline-flex">Ctrl K</span>
      </div>

      {isOpen && (
        <div className="nk-header-search-results">
          <div className="nk-header-search-results-head">
            <span>{query ? "This page and navigation" : "Quick links"}</span>
            <span>{results.length} item{results.length === 1 ? "" : "s"}</span>
          </div>

          {results.length ? (
            <div className="nk-header-search-results-body">
              {results.map((item, index) => (
                <button
                  key={`${item.label}-${item.link}`}
                  type="button"
                  className={`nk-header-search-result${selectedIndex === index ? " is-active" : ""}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onClick={() => selectResult(item)}
                >
                  <span className="nk-header-search-result-icon">
                    <Icon name={item.icon || "search"} />
                  </span>
                  <span className="nk-header-search-result-copy">
                    <span className="nk-header-search-result-title">{item.label}</span>
                    <span className="nk-header-search-result-meta">{item.meta || "This Page"}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="nk-header-search-empty">
              No matches found for <strong>{query}</strong> on this page
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
